import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, InputNumber, Switch, Table, Space, message, Collapse } from 'antd';
import { Project, getProjectQuotaSettings, updateProjectQuotaSettings } from '../services/project';

interface Hospital { name: string; override: number | null; limit: number | null; used: number; remaining: number | null }
interface Audit { id: number; updated_by: number; createdAt: string; before_values: any; after_values: any }
const labels: Record<string, string> = { doctor_id: '专家ID', hospital_limit_enabled: '医院限制', default_hospital_limit: '默认医院总上限', default_doctor_monthly_limit: '默认专家月上限', hospital_quotas: '医院独立额度', max_submissions: '专家累计上限', monthly_max_submissions: '专家月上限' };
const describe = (values: any) => Object.entries(values || {}).map(([k, v]) => `${labels[k] || k}：${v === null ? '未配置' : typeof v === 'boolean' ? (v ? '开启' : '关闭') : typeof v === 'object' ? JSON.stringify(v) : v}`).join('；');

const ProjectQuotaSettings: React.FC<{ projectId: number; onSaved?: () => void }> = ({ projectId, onSaved }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [audits, setAudits] = useState<Audit[]>([]);
  const [overrides, setOverrides] = useState<Record<string, number>>({});
  const enabled = Form.useWatch('hospital_limit_enabled', form);
  const defaultLimit = Form.useWatch('default_hospital_limit', form);
  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    getProjectQuotaSettings(projectId).then(res => {
      if (!active) return;
      if (!res.success) throw new Error(res.message);
      form.setFieldsValue(res.data); setHospitals(res.data.hospitals); setAudits(res.data.audits); setOverrides(res.data.hospital_quotas || {});
    }).catch(e => { if (active) setError(e.response?.data?.message || e.message || '额度配置加载失败'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [projectId, form]);
  const save = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const res = await updateProjectQuotaSettings(projectId, { ...values, hospital_quotas: overrides } as Partial<Project>);
      if (!res.success) throw new Error(res.message);
      message.success('额度配置已保存'); onSaved?.();
      const refreshed = await getProjectQuotaSettings(projectId);
      setHospitals(refreshed.data.hospitals); setAudits(refreshed.data.audits);
    } catch (e: any) {
      if (!e.errorFields) message.error(e.response?.data?.message || e.message || '保存失败');
    } finally { setSaving(false); }
  };
  return <div style={{ marginBottom: 24 }}>
    {error && <Alert type="error" message={error} />}
    <Form form={form} layout="inline" disabled={loading || saving || !!error} style={{ marginBottom: 16 }}>
      <Form.Item name="hospital_limit_enabled" label="开启医院额度限制" valuePropName="checked"><Switch /></Form.Item>
      <Form.Item name="default_hospital_limit" label="默认医院总上限" rules={[{ required: !!enabled, message: '开启后必须配置医院上限' }]}><InputNumber min={1} max={2147483647} precision={0} /></Form.Item>
      <Form.Item name="default_doctor_monthly_limit" label="专家默认每月上限" rules={[{ required: true, message: '请配置本项目专家默认月上限' }]}><InputNumber min={1} max={2147483647} precision={0} /></Form.Item>
      <Button type="primary" onClick={save} loading={saving} disabled={loading || !!error}>保存项目及医院额度</Button>
    </Form>
    <Alert showIcon type="info" style={{ marginBottom: 12 }} message="同一项目按医院名称共用总额度，不按月重置。医院独立额度留空使用项目默认值。关闭医院限制时保留配置及历史计数。提交即占用，拒绝不释放，修改原记录不重复计数。" />
    <Table<Hospital> rowKey="name" size="small" loading={loading} dataSource={hospitals} pagination={{ pageSize: 10 }} columns={[
      { title: '医院名称', dataIndex: 'name' },
      { title: '独立总上限', render: (_, r) => <InputNumber min={1} max={2147483647} precision={0} disabled={loading || saving || !!error} value={overrides[r.name] ?? null} placeholder="使用项目默认" onChange={v => setOverrides(prev => {
        const next = { ...prev }; if (v === null) delete next[r.name]; else next[r.name] = Number(v); return next;
      })} /> },
      { title: '生效上限', render: (_, r) => enabled ? (overrides[r.name] || defaultLimit || '未配置') : '医院限制关闭' },
      { title: '累计已提交', dataIndex: 'used' },
      { title: '剩余', render: (_, r) => enabled && (overrides[r.name] || defaultLimit) ? Math.max(0, (overrides[r.name] || defaultLimit) - r.used) : '-' }
    ]} />
    <Collapse items={[{ key: 'audit', label: '最近50条额度调整记录', children: <Space direction="vertical" style={{ width: '100%' }}>{audits.map(a => <div key={a.id} style={{ borderBottom: '1px solid #eee', paddingBottom: 8 }}>
      <div>{new Date(a.createdAt).toLocaleString('zh-CN')} · 操作人ID {a.updated_by}</div>
      <div>调整前：{describe(a.before_values)}</div><div>调整后：{describe(a.after_values)}</div>
    </div>)}</Space> }]} />
  </div>;
};
export default ProjectQuotaSettings;
